import { CommentRepository, PostRepository } from "@amityco/ts-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate } from "react-router-dom";

import { Header } from "@/app/modules/Navigation";
import { Loader, PageLoader, ScrollToTopButton } from "@/components";
import { useAmityAuth, useTranslate } from "@/providers/context";
import { slugify } from "@/utils";

import { useAppProperties } from "@/hooks";
import {
  BackButton,
  CommunityComment,
  CommunityCommentInput,
  CommunityPost,
  EmptyScreen,
} from "../";
import { SEARCH_PARAM } from "../../const";
import { CommentItem, CommunityGroupWithDetails, PostItem } from "../../types";
import { mapAmityComments, mapAmityPost } from "../../util";

const SCROLL_CONTAINER_ID = "ScrollContainer--CommunityPostDetails";

export const CommunityPostDetails = ({
  postId,
  parentGroup,
}: {
  postId: string;
  parentGroup: CommunityGroupWithDetails;
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { translate } = useTranslate();
  const { communityGroupsEnabled } = useAppProperties();
  const { authState, isError: authIsError } = useAmityAuth();
  const [post, setPost] = useState<PostItem>();
  const [postIsError, setPostIsError] = useState(false);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [onNextPage, setOnNextPage] = useState<() => void>();
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(
    new Set(),
  );

  const goBack = useCallback(() => {
    if (state?.fromQuery) {
      navigate(
        `/community/${slugify(parentGroup.Name)}?${SEARCH_PARAM}=${state.fromQuery}`,
      );
    } else {
      navigate(`/community/${slugify(parentGroup.Name)}`);
    }
  }, [parentGroup, navigate, state?.fromQuery]);

  useEffect(() => {
    if (!authState.authenticatedUser?.userId) {
      return;
    }

    const unsub = PostRepository.getPost(
      postId,
      async ({ data, loading, error }) => {
        if (!loading) {
          if (error) {
            console.error("Failed to fetch post:", error);
            goBack();
          } else if (data) {
            const mappedPost = await mapAmityPost({
              post: data,
              currentUserId: authState.authenticatedUser?.userId,
            });
            setPost(mappedPost);
          }
        }
      },
    );

    return () => unsub();
  }, [postId, authState.authenticatedUser?.userId, navigate, goBack]);

  const handleGetComments = useCallback<
    Amity.LiveCollectionCallback<Amity.InternalComment>
  >(
    async (info) => {
      const { data, loading, error } = info;
      if (!loading) {
        setLoadingNextPage(false);
        if (error) {
          setPostIsError(true);
        } else if (data) {
          const mappedComments = await mapAmityComments({
            comments: data,
            currentUserId: authState.authenticatedUser?.userId,
          });
          setComments(mappedComments);
          setHasNextPage(info.hasNextPage ?? false);
          setOnNextPage(() => info.onNextPage);
          setLoadingNextPage(loading);
        }
      }
    },
    [
      setComments,
      setHasNextPage,
      setOnNextPage,
      setLoadingNextPage,
      setPostIsError,
      authState.authenticatedUser?.userId,
    ],
  );

  useEffect(() => {
    if (!post) {
      return;
    }

    const getCommentsParams: Amity.CommentLiveCollection = {
      referenceType: "post",
      referenceId: post.id,
      dataTypes: {
        values: ["text", "image"],
        matchType: "any",
      },
      limit: 10,
      sortBy: "lastCreated",
    };
    const unsubscribe = CommentRepository.getComments(
      getCommentsParams,
      handleGetComments,
    );
    return unsubscribe;
  }, [post, handleGetComments]);

  const handleNextPage = useCallback(() => {
    if (hasNextPage && onNextPage) {
      setLoadingNextPage(true);
      onNextPage();
    }
  }, [hasNextPage, onNextPage, setLoadingNextPage]);

  const onEndReached = useCallback(() => {
    if (hasNextPage && !loadingNextPage) {
      handleNextPage();
    }
  }, [hasNextPage, loadingNextPage, handleNextPage]);

  const handleToggleExpanded = useCallback((commentId: string) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  }, []);

  const isError = authIsError || postIsError;
  const isLoading = !isError && (authState.state === "loading" || !post);

  const backButtonText = useMemo(() => {
    if (state?.fromQuery) {
      return translate("Search Results");
    }
    if (communityGroupsEnabled) {
      return parentGroup.Name;
    }
    return translate("Feed");
  }, [state?.fromQuery, communityGroupsEnabled, parentGroup.Name, translate]);

  return (
    <div className="h-full">
      <Header
        className="px-7"
        content={<BackButton text={backButtonText} onClick={goBack} />}
      />
      {isLoading ? (
        <PageLoader />
      ) : !post || isError || !authState?.authenticatedUser ? (
        <EmptyScreen screenType="error" />
      ) : (
        <>
          <div
            className="flex h-[calc(100%-94px)] w-full justify-center overflow-y-auto"
            id={SCROLL_CONTAINER_ID}
          >
            <div className="w-[636px]">
              <CommunityPost
                post={post}
                isDetailsScreen={true}
                hideTopBorder={true}
              />
              <div className="flex min-h-[100px] w-full flex-col pl-3">
                <div className="mb-9 h-[1px] w-full bg-border" />
                <div className="p1-bold">{`${translate("Comments")} (${post.comments})`}</div>
                <div className="pb-10 pt-9">
                  <CommunityCommentInput referenceId={post.id} />
                </div>
              </div>
              <div className="pb-[100px]">
                <InfiniteScroll
                  dataLength={comments.length}
                  next={onEndReached}
                  hasMore={hasNextPage}
                  loader={
                    <div className="flex justify-center py-12">
                      <Loader className="h-8 w-8 text-item-tertiary" />
                    </div>
                  }
                  scrollableTarget={SCROLL_CONTAINER_ID}
                  scrollThreshold={0.7}
                >
                  {comments.map((c) => (
                    <CommunityComment
                      key={c.id}
                      comment={c}
                      layer={1}
                      isExpanded={expandedComments.has(c.id)}
                      onToggleExpanded={() => handleToggleExpanded(c.id)}
                    />
                  ))}
                </InfiniteScroll>
              </div>
            </div>
            <ScrollToTopButton scrollContainerId={SCROLL_CONTAINER_ID} />
          </div>
        </>
      )}
    </div>
  );
};
